import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customPayRunGateway } from '../index';
import { PayrollRunStatusBadge } from '../../payroll-review/components/PayrollRunStatusBadge';
import { furthestAvailableStep } from './stepAccess';
import { runStatusLabel, runTypeLabel, type CustomPayRunResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import '../custom-pay-run.css';

export function CustomPayRunListPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<CustomPayRunResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await customPayRunGateway.list());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load custom pay runs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  /** EPN-242: the history page no longer creates a run with default dates. It opens the start page with the run type chosen, so the preparer picks the schedule, period and
   * pay day and sees the duplicate preview before anything is created. */
  function startRun(type: 'regular' | 'bonus') {
    navigate(`/payroll-processing?type=${type}`);
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Custom Pay Runs</h2>
        <div>
          <button type="button" onClick={() => startRun('regular')}>
            New Regular Run
          </button>{' '}
          <button type="button" onClick={() => startRun('bonus')}>
            New Bonus Run
          </button>
        </div>
      </div>

      <p className="muted">
        A guided, staging-only workflow for off-cycle payroll. Nothing started here can transmit a real direct deposit, tax payment, or
        filing. Discarded runs stay in this list, marked Discarded.
      </p>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && runs.length === 0 && <p className="muted">No custom pay runs yet.</p>}

      {!loading && runs.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Period</th>
              <th>Check Date</th>
              <th>Status</th>
              <th>Review Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id}>
                <td>{runTypeLabel(run.runType)}</td>
                <td>
                  {run.periodStartDate} – {run.periodEndDate}
                </td>
                <td>{run.checkDate}</td>
                <td>{runStatusLabel(run.status)}</td>
                <td>
                  <PayrollRunStatusBadge status={run.payrollRunStatus} />
                </td>
                <td>
                  <div className="cpr-history__links">
                    <button type="button" className="link" onClick={() => navigate(`/payroll-processing/${run.id}/${furthestAvailableStep(run)}`)}>
                      Open
                    </button>
                    {run.payrollRunId && <Link to={`/payroll-review/${run.payrollRunId}`}>Review in Payroll Review</Link>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
